import { useState } from 'react';
import { TextInput, Select, Textarea, Radio, Group } from '@mantine/core';
import { IconTrash, IconPencil, IconCheck, IconX, IconPlus } from '@tabler/icons-react';
import { v4 as uuidv4 } from 'uuid';
import type { FamilyMemberLocal, FamilyConditionLocal, FamilyRelationship } from '../../types/familyHistory';
import { RELATIONSHIP_OPTIONS_FLAT, RELATIONSHIP_CODES } from '../../types/familyHistory';

interface FamilyMemberFormProps {
  member: FamilyMemberLocal;
  onChange: (updated: FamilyMemberLocal) => void;
  onRemove: () => void;
  onSubmit: () => void;
  disabled?: boolean;
  isNew?: boolean;
}

// Relationship badge
function getRelationshipLabel(relationship: FamilyRelationship): string {
  return RELATIONSHIP_CODES[relationship]?.display || relationship;
}

export function FamilyMemberForm({
  member,
  onChange,
  onRemove,
  onSubmit,
  disabled = false,
  isNew = false,
}: FamilyMemberFormProps) {
  const [isEditing, setIsEditing] = useState(isNew);

  const handleChange = <K extends keyof FamilyMemberLocal>(
    field: K,
    value: FamilyMemberLocal[K]
  ) => {
    onChange({ ...member, [field]: value });
  };

  const handleConditionChange = (conditionId: string, field: keyof FamilyConditionLocal, value: string) => {
    const updatedConditions = member.conditions.map((c) =>
      c.id === conditionId ? { ...c, [field]: value } : c
    );
    handleChange('conditions', updatedConditions);
  };

  const addCondition = () => {
    const newCondition: FamilyConditionLocal = {
      id: uuidv4(),
      conditionName: '',
    };
    handleChange('conditions', [...member.conditions, newCondition]);
  };

  const removeCondition = (conditionId: string) => {
    handleChange(
      'conditions',
      member.conditions.filter((c) => c.id !== conditionId)
    );
  };

  const handleSave = () => {
    if (member.relationship) {
      onSubmit();
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    if (isNew) {
      onRemove();
    } else {
      setIsEditing(false);
    }
  };

  // View mode - display as card
  if (!isEditing) {
    return (
      <div className="card p-4 group">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400">
                {getRelationshipLabel(member.relationship)}
              </span>
              {member.name && (
                <h4 className="font-medium text-gray-200">{member.name}</h4>
              )}
            </div>

            {/* Living/Deceased status with age */}
            <div className="mt-1 text-sm text-gray-400">
              {member.isDeceased ? (
                <span>
                  Deceased
                  {member.ageOrAgeAtDeath && ` at age ${member.ageOrAgeAtDeath}`}
                  {member.causeOfDeath && ` (${member.causeOfDeath})`}
                </span>
              ) : (
                member.ageOrAgeAtDeath && <span>Age {member.ageOrAgeAtDeath}</span>
              )}
            </div>

            {/* Conditions */}
            {member.conditions.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-gray-500 mb-1">Conditions:</p>
                <ul className="text-sm text-gray-300 space-y-0.5">
                  {member.conditions.map((cond) => (
                    <li key={cond.id} className="flex items-center gap-1">
                      <span className="text-cyan-400">•</span>
                      {cond.conditionName}
                      {cond.ageAtOnset && (
                        <span className="text-gray-500">(diagnosed at {cond.ageAtOnset})</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Notes */}
            {member.notes && (
              <p className="mt-2 text-sm text-gray-500 italic">{member.notes}</p>
            )}
          </div>

          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              disabled={disabled}
              className="p-1.5 rounded-lg text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors disabled:opacity-50"
              title="Edit"
            >
              <IconPencil size={16} />
            </button>
            <button
              type="button"
              onClick={onRemove}
              disabled={disabled}
              className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50"
              title="Delete"
            >
              <IconTrash size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Edit mode - show form
  return (
    <div className="card p-4 border-cyan-500/30">
      <div className="space-y-4">
        {/* Relationship */}
        <Select
          label="Relationship"
          placeholder="Select relationship"
          data={RELATIONSHIP_OPTIONS_FLAT}
          value={member.relationship || null}
          onChange={(val) => handleChange('relationship', val as FamilyRelationship)}
          disabled={disabled}
          required
          classNames={{
            input: 'input',
            label: 'text-sm font-medium text-gray-300 mb-1',
            dropdown: 'bg-gray-900 border-gray-700',
            option: 'hover:bg-gray-800 text-gray-200',
          }}
        />

        {/* Name (optional) */}
        <TextInput
          label="Name (optional)"
          placeholder="e.g., Jane Doe"
          value={member.name || ''}
          onChange={(e) => handleChange('name', e.target.value || undefined)}
          disabled={disabled}
          classNames={{
            input: 'input',
            label: 'text-sm font-medium text-gray-300 mb-1',
          }}
        />

        {/* Living/Deceased status */}
        <div>
          <label className="text-sm font-medium text-gray-300 mb-2 block">Status</label>
          <Radio.Group
            value={member.isDeceased ? 'deceased' : 'living'}
            onChange={(val) => handleChange('isDeceased', val === 'deceased')}
          >
            <Group>
              <Radio
                value="living"
                label="Living"
                disabled={disabled}
                classNames={{
                  radio: 'border-gray-600 bg-gray-800',
                  label: 'text-gray-300',
                }}
              />
              <Radio
                value="deceased"
                label="Deceased"
                disabled={disabled}
                classNames={{
                  radio: 'border-gray-600 bg-gray-800',
                  label: 'text-gray-300',
                }}
              />
            </Group>
          </Radio.Group>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Age */}
          <TextInput
            label={member.isDeceased ? 'Age at Death' : 'Current Age'}
            placeholder={member.isDeceased ? 'e.g., 72' : 'e.g., 65'}
            value={member.ageOrAgeAtDeath || ''}
            onChange={(e) => handleChange('ageOrAgeAtDeath', e.target.value || undefined)}
            disabled={disabled}
            classNames={{
              input: 'input',
              label: 'text-sm font-medium text-gray-300 mb-1',
            }}
          />

          {/* Cause of Death - only if deceased */}
          {member.isDeceased && (
            <TextInput
              label="Cause of Death"
              placeholder="e.g., Heart disease"
              value={member.causeOfDeath || ''}
              onChange={(e) => handleChange('causeOfDeath', e.target.value || undefined)}
              disabled={disabled}
              classNames={{
                input: 'input',
                label: 'text-sm font-medium text-gray-300 mb-1',
              }}
            />
          )}
        </div>

        {/* Conditions section */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-300">Health Conditions</label>
            <button
              type="button"
              onClick={addCondition}
              disabled={disabled}
              className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1 transition-colors disabled:opacity-50"
            >
              <IconPlus size={14} />
              Add Condition
            </button>
          </div>

          {member.conditions.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No conditions added</p>
          ) : (
            <div className="space-y-3">
              {member.conditions.map((condition) => (
                <div key={condition.id} className="flex gap-2 items-start">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                    <TextInput
                      placeholder="Condition name"
                      value={condition.conditionName}
                      onChange={(e) =>
                        handleConditionChange(condition.id, 'conditionName', e.target.value)
                      }
                      disabled={disabled}
                      classNames={{
                        input: 'input',
                      }}
                    />
                    <TextInput
                      placeholder="Age at diagnosis (optional)"
                      value={condition.ageAtOnset || ''}
                      onChange={(e) =>
                        handleConditionChange(condition.id, 'ageAtOnset', e.target.value)
                      }
                      disabled={disabled}
                      classNames={{
                        input: 'input',
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeCondition(condition.id)}
                    disabled={disabled}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50 mt-1"
                    title="Remove condition"
                  >
                    <IconTrash size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Notes */}
        <Textarea
          label="Additional Notes"
          placeholder="Any other details about this family member's health history"
          value={member.notes || ''}
          onChange={(e) => handleChange('notes', e.target.value || undefined)}
          disabled={disabled}
          minRows={2}
          classNames={{
            input: 'input',
            label: 'text-sm font-medium text-gray-300 mb-1',
          }}
        />

        {/* Action buttons */}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={handleCancel}
            disabled={disabled}
            className="px-3 py-1.5 text-sm text-gray-400 hover:text-gray-200 transition-colors disabled:opacity-50 flex items-center gap-1"
          >
            <IconX size={14} />
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={disabled || !member.relationship}
            className="px-3 py-1.5 text-sm bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <IconCheck size={14} />
            {isNew ? 'Add Family Member' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
